import React, { useMemo, useState } from "react";
import { History, Brain, PackageOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { QuizEngine } from "@/components/QuizEngine";
import { lessonRecapContext } from "@/data/curriculum";
import { ManuscriptLinks } from "@/components/ManuscriptLinks";

const NEEDS = {
  singers: ["A quiet space where you can sing freely", "A glass of water", "Headphones or speakers for the play-alongs", "A pencil and paper for notes", "Manuscript paper (download it free below)"],
  drummers: ["A drum kit, practice pad or even a cushion", "Sticks (7A or 5A)", "Headphones or speakers for the drumless play-alongs", "A pencil and paper", "Manuscript paper (download it free below)"],
  guitarists: ["Your guitar, tuned (use a tuner app)", "A pick, plus your fingers", "Headphones or speakers for the play-alongs", "A pencil and paper", "Manuscript paper (download it free below)"],
  pianists: ["A piano or keyboard (61+ keys is ideal)", "A comfortable bench at the right height", "Headphones or speakers for the play-alongs", "A pencil and paper", "Manuscript paper (download it free below)"],
};

// Warm-up covers every earlier lesson in the course and grows as the course goes on.
const pickWarmup = (earlier, seed) => {
  const lessons = earlier.filter((l) => l.quiz?.length);
  const total = lessons.reduce((n, l) => n + l.quiz.length, 0);
  const target = Math.min(20, 3 + lessons.length * 2, total);
  const out = [];
  for (let r = 0; out.length < target && r < 40; r += 1) {
    for (const l of lessons) {
      if (out.length >= target) break;
      const q = l.quiz[(seed + r) % l.quiz.length];
      const id = `w-${l.id}-${q.id}`;
      if (!out.some((o) => o.id === id)) out.push({ ...q, id });
    }
  }
  return out;
};

export const LessonRecap = ({ lesson, instrumentId }) => {
  const { prev, earlier } = lessonRecapContext(lesson.id);
  const warmup = useMemo(() => pickWarmup(earlier, lesson.id), [earlier, lesson.id]);
  const [quizOpen, setQuizOpen] = useState(false);

  if (!prev) {
    return (
      <section className="mt-10 rounded-2xl border border-border bg-secondary p-6" data-testid="lesson-intro-needs">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><PackageOpen size={14} /> Before you start · {lesson.plan?.recap || 2} min</p>
        <h2 className="mt-2 font-serif text-2xl">What you'll need</h2>
        <ul className="mt-3 space-y-2">
          {(NEEDS[instrumentId] || NEEDS.singers).map((n) => <li key={n} className="flex gap-2 text-sm text-muted-foreground"><span className="text-primary">♪</span>{n}</li>)}
        </ul>
        <ManuscriptLinks testId="needs-manuscript" drumPaper={instrumentId === "drummers"} />
      </section>
    );
  }

  return (
    <section className="mt-10 space-y-5" data-testid="lesson-recap">
      <div className="rounded-2xl border border-border bg-secondary p-6">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><History size={14} /> Recap · {lesson.plan?.recap || 2} min</p>
        <h2 className="mt-2 font-serif text-2xl">Last time: {prev.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{prev.summary}</p>
        <ul className="mt-3 space-y-1.5" data-testid="lesson-recap-points">
          {prev.sections.slice(0, 4).map((s) => <li key={s.heading} className="flex gap-2 text-sm text-foreground"><span className="text-primary">✓</span>{s.heading}</li>)}
        </ul>
        {earlier.length > 1 && (
          <details className="mt-3 rounded-xl border border-border bg-background/60 p-3" data-testid="lesson-recap-all">
            <summary className="cursor-pointer text-xs font-semibold text-primary">Summary of all lessons so far ({earlier.length})</summary>
            <ol className="mt-2 space-y-1.5">
              {earlier.map((l, k) => <li key={l.id} className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{k + 1}. {l.title}</span> — {l.summary}</li>)}
            </ol>
          </details>
        )}
        <Link to={`/lessons/${prev.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="lesson-recap-link">
          Revisit that lesson <ArrowRight size={12} />
        </Link>
      </div>
      {warmup.length > 0 && (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="lesson-warmup">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Brain size={14} /> Test your knowledge · {lesson.plan?.warm || 3} min</p>
          <p className="mt-2 text-sm text-muted-foreground">{warmup.length} quick questions covering all {earlier.length} earlier lesson{earlier.length === 1 ? "" : "s"} in this course to warm up your brain.</p>
          {!quizOpen ? (
            <button onClick={() => setQuizOpen(true)} data-testid="lesson-warmup-start"
              className="mt-4 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
              Start warm-up quiz
            </button>
          ) : (
            <div className="mt-4"><QuizEngine questions={warmup} quizId={`warmup-${lesson.id}`} title="Warm-up" /></div>
          )}
        </div>
      )}
    </section>
  );
};
